<template>
  <div
    id="app"
    class="font-Lato"
    :style="{ '--template-rows': templateRows, '--footer-row': footerRow }"
  >
    <navigation v-if="!$route.meta.navless" />
    <router-view />
    <template v-if="!$route.meta.footerless">
      <template v-if="isMobile"><Footer :dispatch="dispatch"/></template>
      <template v-else><FooterDesktop :dispatch="dispatch"/></template>
    </template>
    <CookieBanner
      v-if="!cookieState.hasDecided"
      :send="send"
      :cookieState="cookieState"
    />
    <ScreenDark v-if="!cookieState.hasDecided" />
    <ChangeCountryPopup v-if="popup" :dispatch="dispatch" />
  </div>
</template>

<script>
import Navigation from './layout/Navigation.vue';
import Footer from './layout/Footer.vue';
import FooterDesktop from './layout/FooterDesktop.vue';
import CookieBanner from './components/shared/CookieBanner.vue';
import ScreenDark from './components/shared/ScreenDark.vue';
import shouldShowPopUp from '../utils/popup';
import { enableAnalytics } from './shared/analytics';
import { LOCALSTORAGE_IDS } from './data/constants';
import { specialCaseSpain } from './shared/entityHelpers';
import entity from '../region.json';

const ChangeCountryPopup = () =>
  import(
    /* webpackChunkName: "pop-up" */

    './components/ChangeCountryPopup.vue'
  );
export default {
  name: 'app',
  components: {
    Navigation,
    Footer,
    FooterDesktop,
    CookieBanner,
    ScreenDark,
    ChangeCountryPopup,
  },
  data() {
    return {
      isMobile: window.innerWidth <= 768,
      cookieState: {
        arrow: 'down',
        view: 'small',
        showMore: false,
        allowCookies: false,
        hasDecided: false,
      },
      popup: null,
    };
  },
  computed: {
    templateRows() {
      return this.$route.meta.navless ? 'minmax(100vh, auto) auto' : null;
    },
    footerRow() {
      return this.$route.meta.navless ? '2' : null;
    },
  },
  methods: {
    footerResize() {
      if (window.innerWidth <= 768 && this.isMobile === false) {
        this.isMobile = true;
      } else if (window.innerWidth > 768 && this.isMobile === true) {
        this.isMobile = false;
      }
    },
    checkLocalStorage() {
      const decision = window.localStorage.getItem(
        LOCALSTORAGE_IDS.USER_COOKIES
      );
      if (decision !== null) {
        // user has made their decision
        // if true then user has allowed cookies

        // need to parse, otherwise it will be a string
        const parsedDecision = JSON.parse(decision);
        if (parsedDecision) {
          this.cookieState.allowCookies = true;
        }
        // remove cookies/privacy banner
        this.cookieState.hasDecided = true;
      }
      const popupChoice = window.localStorage.getItem(
        LOCALSTORAGE_IDS.CHANGE_COUNTRY_POPUP
      );
      if (popupChoice) {
        this.popup = false;
      } else {
        this.checkForUserLanguages();
      }
    },
    toggleMoreInfo() {
      // toggle more information text
      this.cookieState.showMore = !this.cookieState.showMore;
      // toggle height of cookie banner
      this.cookieState.view =
        this.cookieState.view === 'small' ? 'big' : 'small';
      // toggle arrow
      this.cookieState.arrow =
        this.cookieState.arrow === 'down' ? 'up' : 'down';
    },
    send(action) {
      switch (action.event) {
        // toggle button for more info
        case 'toggle':
          this.toggleMoreInfo();
          break;
        case 'accept':
          // user has accepted the privacy policy
          window.localStorage.setItem(LOCALSTORAGE_IDS.USER_COOKIES, true);
          this.checkLocalStorage();
          this.connectWithGoogleAnalytics();
          break;
        case 'decline':
          // user has declined the privacy policy
          window.localStorage.setItem(LOCALSTORAGE_IDS.USER_COOKIES, false);
          this.checkLocalStorage();
          break;
        default:
      }
    },
    connectWithGoogleAnalytics() {
      if (this.cookieState.allowCookies) {
        // TODO(greg) clean up
        enableAnalytics()
          .then(() => {})
          .catch(() => {});
      }
    },
    dispatch(message) {
      if (message === 'changeCountry') {
        this.$router.push({ path: `/${this.$i18n.locale}/change-country` });
      } else if (message === 'open') {
        this.popup = true;
        return;
      }
      window.localStorage.setItem(LOCALSTORAGE_IDS.CHANGE_COUNTRY_POPUP, true);
      this.popup = false;
    },
    checkForUserLanguages() {
      // remove once translations are ready for NL
      if (
        shouldShowPopUp() &&
        !specialCaseSpain(entity.entity, this.$i18n.locale)
      ) {
        this.popup = true;
      }
    },
  },
  beforeMount() {
    // Check if the user has already accepted or declined cookies
    // Needs to be done before mount to avoid flickering when removing cookies banner
    this.checkLocalStorage();
  },
  mounted() {
    // Must be run on mount after checking for localStorage in 'beforeMount' lifecycle
    this.connectWithGoogleAnalytics();
    window.addEventListener('resize', this.footerResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.footerResize);
  },
};
</script>

<style scoped>
#app {
  display: grid;
  grid-template-rows: var(--template-rows);
  --app-child-row: 2;
  --footer-row: 3;
  --template-rows: auto minmax(100vh, auto) auto;
}
#app > *:nth-child(2) {
  grid-row: var(--app-child-row);
}
</style>
